import { useEffect, useState } from "react";
import api from "../lib/api";
import { KanbanBoard } from "../components/KanbanBoard";
import { DataPorter } from "../components/DataPorter";
import { LEAD_STAGES, INDIA_CITIES, LEAD_TYPE_META, INTEREST_META, ACTIVITY_META, OWNERS, labelOf } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, MapPin, Building2, Phone, Mail, StickyNote, CalendarClock, ArrowRightCircle, CheckCircle2, Upload, Settings2, Trash2, UserCog, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const SOURCES = ["Cold outreach", "LinkedIn", "Medical conference", "Referral", "Web enquiry", "Health camp", "Partner network"];
const empty = { name: "", lead_type: "doctor", company: "", city: "Chennai", source: "Cold outreach", interest_level: "cold", owner: "", phone: "", email: "", notes: "" };

const ACT_ICON = { call: Phone, email: Mail, note: StickyNote, meeting: CalendarClock, stage_change: ArrowRightCircle, conversion: CheckCircle2 };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

export default function LeadsFunnel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);
  const [ledit, setLEdit] = useState(null);
  const [activities, setActivities] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertClin, setConvertClin] = useState("none");
  const [newAct, setNewAct] = useState({ type: "call", text: "" });
  // rules
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rules, setRules] = useState([]);
  // import
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState("name,lead_type,city,source\nDr Example Kumar,doctor,Chennai,LinkedIn");
  // board filters
  const [boardQuery, setBoardQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");

  const load = () => api.get("/leads").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/clinicians").then((r) => setClinicians(r.data.filter((c) => ["active", "onboarded", "contracted"].includes(c.stage))));
  }, []);

  const move = async (item, stage) => {
    await api.patch(`/leads/${item._id}/stage`, { stage });
    toast.success(`Moved to ${labelOf(LEAD_STAGES, stage)}`);
    load();
  };

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    const { data } = await api.post("/leads", form);
    toast.success(`Lead added · owner ${data.owner}`);
    setOpen(false); setForm(empty); load();
  };

  const openDetail = async (item) => {
    const [d, a] = await Promise.all([
      api.get(`/leads/${item._id}`),
      api.get(`/leads/${item._id}/activities`),
    ]);
    setDetail(d.data); setActivities(a.data);
    setLEdit({ lead_type: d.data.lead_type, interest_level: d.data.interest_level, owner: d.data.owner || "", city: d.data.city || "", source: d.data.source || "", company: d.data.company || "", phone: d.data.phone || "", email: d.data.email || "", notes: d.data.notes || "" });
  };

  const saveLead = async () => {
    await api.patch(`/leads/${detail._id}`, { ...ledit });
    toast.success("Lead updated");
    const { data } = await api.get(`/leads/${detail._id}`); setDetail(data); load();
  };

  const removeLead = async () => {
    if (!window.confirm(`Delete ${detail.name}? This cannot be undone.`)) return;
    await api.delete(`/leads/${detail._id}`);
    toast.success("Lead deleted");
    setDetail(null); load();
  };

  const addActivity = async () => {
    if (!newAct.text.trim()) return toast.error("Add a note first");
    await api.post(`/leads/${detail._id}/activities`, newAct);
    const a = await api.get(`/leads/${detail._id}/activities`);
    setActivities(a.data); setNewAct({ type: "call", text: "" });
    toast.success("Activity logged");
  };

  const convert = async () => {
    if (detail.lead_type === "patient") { setConvertClin("none"); setConvertOpen(true); return; }
    await doConvert();
  };

  const doConvert = async () => {
    const body = detail.lead_type === "patient" && convertClin !== "none" ? { assigned_clinician_id: convertClin } : {};
    const { data } = await api.post(`/leads/${detail._id}/convert`, body);
    toast.success(`Converted to ${LEAD_TYPE_META[data.lead_type].label} record`);
    setConvertOpen(false); setDetail(null); load();
  };

  // ---- Assignment rules ----
  const openRules = async () => {
    const { data } = await api.get("/settings/assignment-rules");
    setRules(data.rules); setRulesOpen(true);
  };
  const saveRules = async () => {
    await api.put("/settings/assignment-rules", { rules });
    toast.success("Assignment rules saved");
    setRulesOpen(false);
  };
  const addRule = () => setRules([...rules, { match_type: "city", value: "Chennai", owner: OWNERS[0] }]);
  const updateRule = (i, k, v) => setRules(rules.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const removeRule = (i) => setRules(rules.filter((_, idx) => idx !== i));

  // ---- Import ----
  const doImport = async () => {
    const { data } = await api.post("/leads/import", { csv: csvText });
    toast.success(`Imported ${data.created} leads`);
    setImportOpen(false); load();
  };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(f);
  };

  const openCount = items.filter((i) => !["converted", "lost"].includes(i.stage)).length;
  const convertedCount = items.filter((i) => i.stage === "converted").length;

  const filteredItems = items.filter((l) => {
    const q = boardQuery.trim().toLowerCase();
    const matchQ = !q || [l.name, l.company, l.city, l.owner, l.email, l.phone, l.source].some((v) => (v || "").toLowerCase().includes(q));
    return matchQ && (stageFilter === "all" || l.stage === stageFilter) && (ownerFilter === "all" || l.owner === ownerFilter);
  });
  const visibleStages = stageFilter === "all" ? LEAD_STAGES : LEAD_STAGES.filter((s) => s.key === stageFilter);
  const ownerOptions = Array.from(new Set(items.map((l) => l.owner).filter(Boolean))).sort();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-head text-ccc-navy" data-testid="leads-title">Sales Pipeline</h1>
          <p className="text-sm text-slate-500 mt-1">Cold leads → conversion · {openCount} open · {convertedCount} converted</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <DataPorter entity="leads" showImport={false} onImported={load} />
          <Button data-testid="assignment-rules-button" variant="outline" onClick={openRules}><UserCog className="h-4 w-4 mr-1.5" /> Assignment Rules</Button>
          <Button data-testid="import-leads-button" variant="outline" onClick={() => setImportOpen(true)}><Upload className="h-4 w-4 mr-1.5" /> Import CSV</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-lead-button" className="bg-ccc-blue hover:bg-ccc-blue2 text-white"><Plus className="h-4 w-4 mr-1.5" /> Add Lead</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Add Sales Lead</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="col-span-2"><Label>Name</Label><Input data-testid="lead-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
                <div><Label>Lead type</Label>
                  <Select value={form.lead_type} onValueChange={(v) => setForm({ ...form, lead_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="lead-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="doctor">Doctor</SelectItem>
                      <SelectItem value="clinician">Clinician</SelectItem>
                      <SelectItem value="patient">Patient</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Interest</Label>
                  <Select value={form.interest_level} onValueChange={(v) => setForm({ ...form, interest_level: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{["cold", "warm", "hot"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Affiliation / Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1" /></div>
                <div><Label>City</Label>
                  <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{INDIA_CITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Source</Label>
                  <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Owner <span className="text-slate-400 font-normal">(auto if blank)</span></Label>
                  <Select value={form.owner || "auto"} onValueChange={(v) => setForm({ ...form, owner: v === "auto" ? "" : v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-assign</SelectItem>
                      {OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
                <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
              </div>
              <DialogFooter><Button data-testid="lead-save-button" onClick={create} className="bg-ccc-blue hover:bg-ccc-blue2 text-white">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="board-filters">
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="board-search" value={boardQuery} onChange={(e) => setBoardQuery(e.target.value)} placeholder="Search leads…" className="h-9 pl-9" />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-48 h-9" data-testid="board-stage-filter"><SelectValue placeholder="All stages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {LEAD_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={ownerFilter} onValueChange={setOwnerFilter}>
          <SelectTrigger className="w-44 h-9" data-testid="board-owner-filter"><SelectValue placeholder="All owners" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All owners</SelectItem>
            {ownerOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        {(boardQuery || stageFilter !== "all" || ownerFilter !== "all") && (
          <button data-testid="board-clear-filter" onClick={() => { setBoardQuery(""); setStageFilter("all"); setOwnerFilter("all"); }} className="text-xs text-slate-400 hover:text-ccc-navy">Clear</button>
        )}
        <span className="text-xs text-slate-400 ml-auto">{filteredItems.length} of {items.length}</span>
      </div>

      <KanbanBoard
        stages={visibleStages}
        items={filteredItems}
        accent="#10B981"
        onMove={move}
        onCardClick={openDetail}
        renderCard={(l) => {
          const tm = LEAD_TYPE_META[l.lead_type] || LEAD_TYPE_META.doctor;
          const im = INTEREST_META[l.interest_level] || INTEREST_META.cold;
          return (
            <div>
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-sm text-ccc-navy leading-tight">{l.name}</p>
                <Badge className={`${im.cls} border-0 text-[10px]`}>{im.label}</Badge>
              </div>
              <div className="mt-1 flex items-center gap-1.5">
                <Badge className={`${tm.cls} border-0 text-[10px]`}>{tm.label}</Badge>
                {l.converted_to_id && <CheckCircle2 className="h-3.5 w-3.5 text-ccc-emerald" />}
              </div>
              <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1"><MapPin className="h-3 w-3" />{l.city} · {l.source}</p>
              <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1"><UserCog className="h-3 w-3" />{l.owner || "Unassigned"}</p>
            </div>
          );
        }}
      />

      {/* Assignment rules dialog */}
      <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="h-4 w-4" /> Owner Assignment Rules</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">New/imported leads with no owner are routed by the first matching rule (city rules take priority, then lead type).</p>
          <div className="space-y-2 mt-2 max-h-80 overflow-y-auto" data-testid="rules-list">
            {rules.map((r, i) => (
              <div key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                <Select value={r.match_type} onValueChange={(v) => updateRule(i, "match_type", v)}>
                  <SelectTrigger className="w-28 h-9 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="city">City</SelectItem><SelectItem value="lead_type">Lead type</SelectItem></SelectContent>
                </Select>
                {r.match_type === "city" ? (
                  <Select value={r.value} onValueChange={(v) => updateRule(i, "value", v)}>
                    <SelectTrigger className="flex-1 h-9 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{INDIA_CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Select value={r.value} onValueChange={(v) => updateRule(i, "value", v)}>
                    <SelectTrigger className="flex-1 h-9 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{["doctor", "clinician", "patient"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <span className="text-xs text-slate-400">→</span>
                <Select value={r.owner} onValueChange={(v) => updateRule(i, "owner", v)}>
                  <SelectTrigger className="w-32 h-9 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => removeRule(i)} className="text-slate-400 hover:text-ccc-rose"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {rules.length === 0 && <p className="text-xs text-slate-400">No rules — leads stay Unassigned.</p>}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="add-rule-button" onClick={addRule}><Plus className="h-4 w-4 mr-1" /> Add rule</Button>
            <Button data-testid="save-rules-button" onClick={saveRules} className="bg-ccc-blue hover:bg-ccc-blue2 text-white">Save rules</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert patient dialog — assign a clinician on conversion */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowRightCircle className="h-4 w-4" /> Convert to Patient</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Assign a clinician for this new patient (optional — you can also assign later from the patient record).</p>
          <div>
            <Label>Assign clinician</Label>
            <Select value={convertClin} onValueChange={setConvertClin}>
              <SelectTrigger className="mt-1" data-testid="convert-clinician-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Assign later</SelectItem>
                {clinicians.map((c) => <SelectItem key={c._id} value={c._id}>{c.name} · {c.city}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter><Button data-testid="convert-confirm-button" onClick={doConvert} className="bg-ccc-emerald hover:bg-emerald-600 text-white">Convert to patient</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="h-4 w-4" /> Import Cold Leads (CSV)</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Columns: <code className="text-ccc-blue">name, lead_type, city, source, company, owner, phone, email, interest_level</code>. Only <b>name</b> is required. Blank owners are auto-assigned by your rules.</p>
          <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="import-file-input" className="text-sm" />
          <Textarea data-testid="import-csv-textarea" value={csvText} onChange={(e) => setCsvText(e.target.value)} className="font-mono text-xs h-40" />
          <DialogFooter><Button data-testid="import-submit-button" onClick={doImport} className="bg-ccc-emerald hover:bg-emerald-600 text-white">Import leads</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail sheet */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-head">{detail.name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4" data-testid="lead-detail">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-ccc-navy text-white border-0">{labelOf(LEAD_STAGES, detail.stage)}</Badge>
                  <Badge className={`${(LEAD_TYPE_META[detail.lead_type] || {}).cls} border-0`}>{(LEAD_TYPE_META[detail.lead_type] || {}).label}</Badge>
                  <Badge className={`${(INTEREST_META[detail.interest_level] || {}).cls} border-0`}>{(INTEREST_META[detail.interest_level] || {}).label}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <LF label="Owner" value={detail.owner} icon={UserCog} />
                  <LF label="City" value={detail.city} icon={MapPin} />
                  <LF label="Affiliation" value={detail.company} icon={Building2} />
                  <LF label="Source" value={detail.source} />
                  <LF label="Phone" value={detail.phone} icon={Phone} />
                  <LF label="Email" value={detail.email} icon={Mail} />
                </div>
                {detail.notes && <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 border border-slate-100">{detail.notes}</p>}

                {ledit && (
                <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-3" data-testid="lead-edit">
                  <p className="text-xs font-semibold uppercase text-slate-400">Modify</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Lead type</Label><Select value={ledit.lead_type} onValueChange={(v) => setLEdit({ ...ledit, lead_type: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{["doctor", "clinician", "patient"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Interest</Label><Select value={ledit.interest_level} onValueChange={(v) => setLEdit({ ...ledit, interest_level: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{["cold", "warm", "hot"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Owner</Label><Select value={ledit.owner || "none"} onValueChange={(v) => setLEdit({ ...ledit, owner: v === "none" ? "" : v })}><SelectTrigger className="mt-1" data-testid="lead-edit-owner"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">Unassigned</SelectItem>{OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>City</Label><Select value={ledit.city || undefined} onValueChange={(v) => setLEdit({ ...ledit, city: v })}><SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{INDIA_CITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Source</Label><Select value={ledit.source || undefined} onValueChange={(v) => setLEdit({ ...ledit, source: v })}><SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Affiliation</Label><Input value={ledit.company} onChange={(e) => setLEdit({ ...ledit, company: e.target.value })} className="mt-1" /></div>
                    <div><Label>Phone</Label><Input value={ledit.phone} onChange={(e) => setLEdit({ ...ledit, phone: e.target.value })} className="mt-1" /></div>
                    <div><Label>Email</Label><Input value={ledit.email} onChange={(e) => setLEdit({ ...ledit, email: e.target.value })} className="mt-1" /></div>
                    <div className="col-span-2"><Label>Notes</Label><Textarea value={ledit.notes} onChange={(e) => setLEdit({ ...ledit, notes: e.target.value })} className="mt-1" /></div>
                  </div>
                  <Button data-testid="save-lead-detail" onClick={saveLead} className="w-full bg-ccc-blue hover:bg-ccc-blue2 text-white">Save changes</Button>
                  {isAdmin && (
                    <Button data-testid="delete-lead-button" variant="outline" onClick={removeLead} className="w-full border-rose-300 text-ccc-rose hover:bg-rose-50"><Trash2 className="h-4 w-4 mr-2" />Delete lead</Button>
                  )}
                </div>
                )}

                {detail.converted_to_id ? (
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center">
                    <CheckCircle2 className="h-6 w-6 text-ccc-emerald mx-auto" />
                    <p className="text-sm font-semibold text-ccc-navy mt-1">Converted</p>
                    <p className="text-xs text-slate-500">A {(LEAD_TYPE_META[detail.lead_type] || {}).label} record was created from this lead.</p>
                  </div>
                ) : (
                  <Button data-testid="convert-lead-button" onClick={convert} className="w-full bg-ccc-emerald hover:bg-emerald-600 text-white">
                    <ArrowRightCircle className="h-4 w-4 mr-2" /> Convert to {(LEAD_TYPE_META[detail.lead_type] || {}).label}
                  </Button>
                )}

                {/* Activity timeline */}
                <div className="pt-2">
                  <p className="text-xs font-semibold uppercase text-slate-400 mb-2">Activity & Outreach History</p>
                  <div className="flex gap-2 mb-3">
                    <Select value={newAct.type} onValueChange={(v) => setNewAct({ ...newAct, type: v })}>
                      <SelectTrigger className="w-28 h-9" data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{["call", "email", "note", "meeting"].map((t) => <SelectItem key={t} value={t}>{ACTIVITY_META[t].label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input data-testid="activity-text-input" value={newAct.text} onChange={(e) => setNewAct({ ...newAct, text: e.target.value })} placeholder="Log a call, email or note…" className="flex-1 h-9" onKeyDown={(e) => e.key === "Enter" && addActivity()} />
                    <Button data-testid="activity-add-button" onClick={addActivity} className="h-9 bg-ccc-blue hover:bg-ccc-blue2 text-white px-3"><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-3" data-testid="activity-timeline">
                    {activities.map((a) => {
                      const Icon = ACT_ICON[a.type] || StickyNote;
                      const meta = ACTIVITY_META[a.type] || ACTIVITY_META.note;
                      return (
                        <div key={a._id} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <div className="h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}18` }}>
                              <Icon className="h-3.5 w-3.5" style={{ color: meta.color }} />
                            </div>
                            <div className="flex-1 w-px bg-slate-200 my-1" />
                          </div>
                          <div className="pb-1 min-w-0">
                            <p className="text-sm text-ccc-navy leading-snug">{a.text}</p>
                            <p className="text-[11px] text-slate-400 mt-0.5">{meta.label} · {a.owner} · {fmtDate(a.created_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                    {activities.length === 0 && <p className="text-xs text-slate-400">No activity yet.</p>}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const LF = ({ label, value, icon: Icon }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-sm text-ccc-navy font-medium flex items-center gap-1 mt-0.5">{Icon && <Icon className="h-3 w-3 text-slate-400" />}{value || "—"}</p>
  </div>
);
